import { useEffect, useMemo, useState } from "react";
import { createDrawHostSocket } from "./connection";
import { createStrokeStore } from "./strokes";

// A reload of the question page must find its session again, otherwise the
// drawer keeps drawing into a session nobody is watching.
const savedKey = (qrCodeId) => `draw_host_${qrCodeId}`;
const readSaved = (qrCodeId) => {
  try {
    return JSON.parse(sessionStorage.getItem(savedKey(qrCodeId)));
  } catch {
    return null;
  }
};

/**
 * Board side of a draw session.
 * phase: "connecting" | "waiting" (show the QR) | "drawing" | "error"
 */
const useDrawHost = (qrCodeId) => {
  const store = useMemo(() => createStrokeStore(), []);
  const [phase, setPhase] = useState("connecting");
  const [qrDataUrl, setQrDataUrl] = useState(null);
  const [drawerConnected, setDrawerConnected] = useState(true);

  useEffect(() => {
    if (!qrCodeId) return;
    const socket = createDrawHostSocket();
    let session = readSaved(qrCodeId); // { code, hostKey, qrDataUrl }
    // True once the server confirmed this mount owns the session. Cleanup
    // only ends a confirmed session — an effect torn down before it connected
    // (StrictMode's double mount) must leave the saved one for the next mount.
    let confirmed = false;

    const create = () =>
      socket.emit("host:create", { qrCodeId }, (res) => {
        if (!res?.ok) return setPhase("error");
        session = {
          code: res.code,
          hostKey: res.hostKey,
          qrDataUrl: res.qrDataUrl,
        };
        try {
          sessionStorage.setItem(savedKey(qrCodeId), JSON.stringify(session));
        } catch {}
        confirmed = true;
        store.reset([]);
        setQrDataUrl(res.qrDataUrl);
        setPhase("waiting");
      });

    socket.on("connect", () => {
      if (!session) return create();
      const { code, hostKey } = session;
      socket.emit("host:resume", { code, hostKey }, (res) => {
        if (!res?.ok) return create(); // expired — start over with a new QR
        confirmed = true;
        store.reset(res.strokes);
        setQrDataUrl(session.qrDataUrl);
        setPhase(res.status === "drawing" ? "drawing" : "waiting");
      });
    });
    socket.on("connect_error", () => {
      if (!session) setPhase("error");
    });

    socket.on("draw:started", () => {
      setDrawerConnected(true);
      setPhase("drawing");
    });
    socket.on("draw:presence", ({ connected }) => setDrawerConnected(connected));
    socket.on("stroke:start", (p) => store.start(p));
    socket.on("stroke:pts", (p) => store.pts(p));
    socket.on("stroke:undo", () => store.undo());
    socket.on("stroke:clear", () => store.clear());

    return () => {
      if (confirmed) {
        socket.emit("host:end", session);
        try {
          sessionStorage.removeItem(savedKey(qrCodeId));
        } catch {}
      }
      socket.disconnect();
    };
  }, [qrCodeId, store]);

  return { phase, qrDataUrl, store, drawerConnected };
};

export default useDrawHost;
